import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { notificationService } from '../services/notification/notification-provider';
import {
  MarkNotificationsReadRequest,
  NotificationListResponse,
  NotificationSearchRequest,
  NotificationSummaryResponse,
} from '../services/service-trips-api/types/notification';
import { QueryKeys } from './types';

export const useNotificationSummaryQuery = (): UseQueryResult<NotificationSummaryResponse> =>
  useQuery({
    queryKey: [QueryKeys.NotificationSummary],
    queryFn: () => notificationService.getSummary(),
    refetchInterval: 30_000,
  });

export const useNotificationListQuery = (
  request: NotificationSearchRequest
): UseQueryResult<NotificationListResponse> =>
  useQuery({
    queryKey: [
      QueryKeys.ListNotifications,
      request.searchString,
      request.status,
      request.pageNumber,
      request.itemsPerPage,
    ],
    queryFn: () => notificationService.getNotifications(request),
  });

export const useMarkNotificationsReadMutation = (): UseMutationResult<
  void,
  Error,
  MarkNotificationsReadRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MarkNotificationsReadRequest) => {
      await notificationService.markAsRead(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.NotificationSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ListNotifications],
      });
    },
  });
};

export const useMarkAllNotificationsReadMutation = (): UseMutationResult<
  void,
  Error,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await notificationService.markAllAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.NotificationSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ListNotifications],
      });
    },
  });
};

interface NotificationPushState {
  hasNew: boolean;
  pushSignal: number;
  acknowledge: () => void;
  dismiss: () => void;
}

export const useNotificationPush = (): NotificationPushState => {
  const queryClient = useQueryClient();
  const [hasNew, setHasNew] = useState(false);
  const [pushSignal, setPushSignal] = useState(0);
  const lastSeenCountRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const { mutate: markAllAsRead } = useMarkAllNotificationsReadMutation();

  const acknowledgingRef = useRef(false);

  const acknowledge = useCallback(() => {
    if (acknowledgingRef.current) return;
    acknowledgingRef.current = true;
    markAllAsRead(undefined, {
      onSettled: () => {
        acknowledgingRef.current = false;
      },
    });
    lastSeenCountRef.current = 0;
    setHasNew(false);
  }, [markAllAsRead]);

  /** Reset the "new" indicator without marking notifications as read. */
  const dismiss = useCallback(() => {
    setHasNew(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    notificationService.startPush();

    // Set initial baseline before subscribing to updates
    Promise.resolve(notificationService.getSummary())
      .then((initial) => {
        if (mountedRef.current) {
          lastSeenCountRef.current = initial.totalUnread;
        }
      })
      .catch(() => {
        // Silently ignore — API service may not be available yet
      });

    const unsubscribe = notificationService.onUpdate(() => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.NotificationSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ListNotifications],
      });

      Promise.resolve(notificationService.getSummary())
        .then((summary) => {
          if (!mountedRef.current) return;
          if (
            lastSeenCountRef.current !== null &&
            summary.totalUnread > lastSeenCountRef.current
          ) {
            setHasNew(true);
            setPushSignal((c) => c + 1);
          }
          lastSeenCountRef.current = summary.totalUnread;
        })
        .catch(() => {
          // Silently ignore — API service may not be available yet
        });
    });

    return () => {
      mountedRef.current = false;
      unsubscribe();
      notificationService.stopPush();
    };
  }, [queryClient]);

  return { hasNew, pushSignal, acknowledge, dismiss };
};
